import React, { FC, useCallback, useRef, useState } from 'react';

import { useClickOutside } from 'hooks/useClickOutside';
import { useDebounce } from 'hooks/useDebounce';
import { useSimpleToggle } from 'hooks/useSimpleToggle';

import { FilterDropdownButton } from './button/index';
import { FilterOpen } from './filter-open/index';
import { FilterDropdownProps, FilterOption } from './types';

/**
 * FilterDropdown provides a generic dropdown interface for filtering.
 * It supports search, selection, and infinite scrolling of options.
 */
export const FilterDropdown: FC<FilterDropdownProps<FilterOption>> = ({
  label,
  labelledBy,
  selected,
  setSelected,
  showLabel,
  name,
  placeholder,
  searchPlaceholder,
  options,
}) => {
  const { off, showContent: open, toggle: openUp } = useSimpleToggle();
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSetQuery = useDebounce(setSearchQuery, 500);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, off);

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, options]);

  const fetchNextPage = useCallback(() => {
    // TODO: Implement pagination logic when backend is ready
  }, []);

  const dropdownOptions = filteredOptions;

  return (
    <div ref={containerRef} className="relative">
      <div role="presentation" onClick={e => e.stopPropagation()}>
        <FilterDropdownButton
          label={label}
          labelledBy={labelledBy}
          name={name}
          placeholder={placeholder}
          open={open}
          openUp={openUp}
          count={selected.length}
          clearFilter={() => setSelected([])}
          labels={selected.map(select => select.label)}
          showLabel={showLabel}
          setSelected={setSelected}
          selected={selected}
        />
        {open && (
          <FilterOpen
            searchPlaceholder={searchPlaceholder}
            selected={selected}
            setSelected={setSelected}
            options={dropdownOptions}
            setQuery={debounceSetQuery}
            fetchNextPage={fetchNextPage}
          />
        )}
      </div>
    </div>
  );
};
